import React, { useState } from 'react';
import styled from 'styled-components';
import { useUser } from '../../contexts/UserContext';
import { config } from '../../config';
import { Link } from 'react-router-dom';

interface CommentFormProps {
  onSubmit: (content: string) => Promise<void>;
  onCancel?: () => void;
  submitting: boolean;
  placeholder?: string;
  compact?: boolean;
}

const FormContainer = styled.div<{ $compact?: boolean }>`
  background-color: #2a2a2a;
  border-radius: 8px;
  padding: ${props => props.$compact ? '12px' : '16px'};
  margin-bottom: ${props => props.$compact ? '0' : '20px'};
  border: 1px solid #444;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const ProfileImage = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: #444;
  object-fit: cover;
`;

const DefaultAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #4CAF50;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 14px;
`;

const Username = styled.span`
  color: #4CAF50;
  font-weight: 500;
  font-size: 0.9rem;
`;

const ProfileLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  color: inherit;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 0.8;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  background-color: #1a1a1a;
  color: #ffffff;
  border: 1px solid #444;
  border-radius: 6px;
  padding: 12px;
  font-size: 0.9rem;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
  
  &::placeholder {
    color: #888;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const FormActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
`;

const CharacterCount = styled.span<{ $isOverLimit?: boolean }>`
  font-size: 0.8rem;
  color: ${props => props.$isOverLimit ? '#f44336' : '#999'};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const SubmitButton = styled.button`
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background-color: #45a049;
  }
  
  &:disabled {
    background-color: #666;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  background: none;
  color: #999;
  border: 1px solid #444;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    color: #ccc;
    border-color: #666;
  }
`;

const CommentForm: React.FC<CommentFormProps> = ({
  onSubmit,
  onCancel,
  submitting,
  placeholder = "Write a comment...",
  compact = false
}) => {
  const { user } = useUser();
  const [content, setContent] = useState('');
  const maxLength = 2000;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!content.trim() || submitting) return;
    
    try {
      await onSubmit(content.trim());
      setContent(''); // Clear form on success
    } catch (error) {
      // Error handling is done in parent component
    }
  };

  const handleCancel = () => {
    setContent('');
    if (onCancel) {
      onCancel();
    }
  };

  const isOverLimit = content.length > maxLength;
  const canSubmit = content.trim().length > 0 && !isOverLimit && !submitting;

  // Get user's initials for default avatar
  const getUserInitials = (username?: string) => {
    if (!username) return '??';
    return username.substring(0, 2).toUpperCase();
  };

  if (!user) {
    return null;
  }

  return (
    <FormContainer $compact={compact}>
      {!compact && (
        <UserInfo>
          <ProfileLink to={`/profile/${user.username}`}>
            {user.profile_image ? (
              <ProfileImage 
                src={`https://${config.awsS3Avatars}.s3.${config.awsRegion}.amazonaws.com/48/${user.profile_image}`} 
                alt={user.username}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const nextSibling = target.nextElementSibling as HTMLElement;
                  if (nextSibling) {
                    nextSibling.style.display = 'flex';
                  }
                }}
              />
            ) : null}
            <DefaultAvatar style={{ display: user.profile_image ? 'none' : 'flex' }}>
              {getUserInitials(user.username)}
            </DefaultAvatar>
            <Username>{user.username}</Username>
          </ProfileLink>
        </UserInfo>
      )}
      
      <form onSubmit={handleSubmit}>
        <TextArea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={placeholder}
          disabled={submitting}
          maxLength={maxLength + 100} // Allow slight overflow for warning
        />
        
        <FormActions>
          <CharacterCount $isOverLimit={isOverLimit}>
            {content.length}/{maxLength}
          </CharacterCount>
          
          <ButtonGroup>
            {onCancel && (
              <CancelButton type="button" onClick={handleCancel}>
                Cancel
              </CancelButton>
            )}
            <SubmitButton type="submit" disabled={!canSubmit}>
              {submitting ? 'Posting...' : 'Post Comment'}
            </SubmitButton>
          </ButtonGroup>
        </FormActions>
      </form>
    </FormContainer>
  );
};

export default CommentForm;